import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from './context/AuthContext';
import { Navbar, Footer, Hero, HowItWorks, CTA, ToastContainer, ConfirmDialogHost } from './components/v2';
import SEO from './components/SEO';

const ExecutorProfile = lazy(() => import('./components/profiles/ExecutorProfile'));
const CustomerProfile = lazy(() => import('./components/profiles/CustomerProfile'));
const RegistrationPage = lazy(() => import('./pages/RegistrationPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CreateOrderPage = lazy(() => import('./pages/CreateOrderPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-surface-0">
    <div className="flex flex-col items-center gap-4">
      <div className="w-9 h-9 rounded-full border-2 border-surface-4 border-t-accent animate-spin" />
      <p className="text-sm text-text-tertiary">Загрузка платформы…</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/" replace />;
  return children;
};

const ProfilePage = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'executor') return <ExecutorProfile user={user} />;
  if (user.role === 'customer') return <CustomerProfile user={user} />;
  return <div className="min-h-screen grid place-items-center bg-surface-0"><h1 className="text-2xl font-bold">{user.full_name}</h1></div>;
};

const LandingPage = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user) return <Navigate to="/profile" replace />;
  return (
    <div className="min-h-screen bg-surface-0 overflow-x-hidden">
      <SEO
        title="Аренда спецтехники рядом с вами"
        description="ТехАрена — платформа для аренды спецтехники напрямую между заказчиками и исполнителями. Без посредников, проверенные исполнители, доступно 24/7."
        image="/hero.webp"
      />
      <Navbar />
      <main id="main-content">
        <Hero />
        <HowItWorks />
        <CTA />
      </main>
      <Footer />
    </div>
  );
};

const AuthLayout = ({ children }) => (
  <div className="min-h-screen bg-surface-0 bg-grid flex flex-col">
    <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_0%,rgba(199,243,107,.08),transparent_34%)]" />
    <div className="relative flex-1 flex items-center justify-center p-4 py-10">{children}</div>
  </div>
);

function App() {
  const { user } = useAuth();
  const [telegramChecked, setTelegramChecked] = useState(false);

  useEffect(() => {
    if (user) { setTelegramChecked(true); return; }
    const tg = window.Telegram?.WebApp;
    if (tg?.initDataUnsafe?.user) {
      const telegramUser = tg.initDataUnsafe.user;
      localStorage.setItem('telegram_user', JSON.stringify({
        id: telegramUser.id,
        first_name: telegramUser.first_name,
        last_name: telegramUser.last_name || '',
        username: telegramUser.username || '',
      }));
      if (tg.initData) {
        localStorage.setItem('telegram_init_data', tg.initData);
      }
    }
    setTelegramChecked(true);
  }, [user]);

  if (!telegramChecked) return <LoadingSpinner />;

  return (
    <HelmetProvider>
      <ToastContainer />
      <ConfirmDialogHost />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
          <Route path="/register" element={<AuthLayout><RegistrationPage /></AuthLayout>} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/create-order" element={<ProtectedRoute><CreateOrderPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </HelmetProvider>
  );
}

export default App;
